import React, { createContext, useContext, useEffect, useState } from 'react';
import { PublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { setTelemetryUser } from '../telemetry';
import { notificationStore } from '../services/store';

/** Returns true when running inside an iframe (e.g. LEAD platform). */
function inIframe(): boolean {
  try { return window !== window.top; } catch { return true; }
}

// Phase 1: use standard Graph scope (no custom API scope needed for localStorage-only mode).
// Phase 2 TODO: replace with api://${VITE_AZURE_CLIENT_ID}/VendorManagement.Read once the
// scope is registered and exposed in the Azure AD app registration.
const SCOPES = ['User.Read'];

const msalConfig = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID}`,
    redirectUri: `${window.location.origin}/auth/callback`,
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

interface AuthCtx { account: AccountInfo | null; login: () => void; logout: () => void; }
const AuthContext = createContext<AuthCtx>({ account: null, login: () => {}, logout: () => {} });

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [account, setAccount] = useState<AccountInfo | null>(null);

  function resolveAccount(acct: AccountInfo) {
    setAccount(acct);
    setTelemetryUser(acct.username, acct.localAccountId);
    notificationStore.checkExpirations(acct.username, import.meta.env.VITE_DM_EMAIL ?? acct.username);
    acquireToken(acct);
  }

  useEffect(() => {
    msalInstance.initialize().then(async () => {
      // Handle post-redirect callback (no-op inside an iframe)
      const resp = await msalInstance.handleRedirectPromise();
      if (resp) {
        resolveAccount(resp.account);
        return;
      }

      // Use a cached account if available
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        resolveAccount(accounts[0]);
        return;
      }

      // No cached account — try SSO silent (user is already logged in via LEAD/Entra)
      try {
        const silentResp = await msalInstance.ssoSilent({ scopes: SCOPES });
        resolveAccount(silentResp.account!);
      } catch {
        // Silent failed; use popup in iframe, redirect elsewhere
        if (!inIframe()) {
          msalInstance.loginRedirect({ scopes: SCOPES });
        }
        // In iframe: leave account null — the UI will show a login button
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function acquireToken(acct: AccountInfo) {
    try {
      const result = await msalInstance.acquireTokenSilent({ scopes: SCOPES, account: acct });
      sessionStorage.setItem('vm_access_token', result.accessToken);
    } catch {
      // Silent refresh failed — use popup in iframe, redirect elsewhere
      if (inIframe()) {
        try {
          const result = await msalInstance.acquireTokenPopup({ scopes: SCOPES, account: acct });
          sessionStorage.setItem('vm_access_token', result.accessToken);
        } catch (e) {
          console.warn('[VendorMgmt] Token refresh failed:', e);
        }
      } else {
        msalInstance.acquireTokenRedirect({ scopes: SCOPES });
      }
    }
  }

  const login = () => {
    if (inIframe()) {
      msalInstance.loginPopup({ scopes: SCOPES })
        .then(resp => resolveAccount(resp.account))
        .catch(e => console.warn('[VendorMgmt] loginPopup failed:', e));
    } else {
      msalInstance.loginRedirect({ scopes: SCOPES });
    }
  };

  const logout = () => {
    sessionStorage.removeItem('vm_access_token');
    msalInstance.logoutRedirect();
  };

  return <AuthContext.Provider value={{ account, login, logout }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
